// AI 工具执行器（前端）：所有数据读写走 window.api（复用现有 REST），执行成功后回调刷新界面。
// query：直接读 window.api；execute：校验 → 禁止 chapter 元数据 → 审批 → 写库 → 建 ai_modify 版本 → 回写编辑器 → 刷新面板。

import { XIAOTIANSHU_TOOLS, mapLegacyToolArgs, resolveToolName } from './tools';
import { validateExecuteActions, formatSchedulerErrors, getHelpContent } from './scheduler';
import { debugLogger } from '../utils/debugLogger';
import { ensureVersionQuota } from '../utils/chapterVersions';
import { writeChapterContentToEditor } from '../utils/editorContent';
import type { AgentContext, ApprovalRequest } from '../types';

export async function executeXiaotianshuTool(
  toolNameRaw: string,
  toolArgsRaw: Record<string, unknown>,
  context: AgentContext,
): Promise<unknown> {
  // resolveToolName 对未知工具抛错；对遗留别名返回规范名。名称被改写时才做参数映射。
  const toolName = resolveToolName(toolNameRaw);
  const toolArgs = toolName !== toolNameRaw ? mapLegacyToolArgs(toolName, toolArgsRaw) : toolArgsRaw;

  switch (toolName) {
    case 'query': {
      const scope = (toolArgs.scope as string | undefined) ?? 'context';
      const filters = (toolArgs.filters as Record<string, unknown> | undefined) ?? {};
      const detailLevel = (toolArgs.detailLevel as string | undefined) ?? 'normal';

      switch (scope) {
        case 'context': {
          // 分层上下文注入：默认只给"简要索引 + 当前章节全文"，避免长篇小说数据量
          // 超出模型上下文窗口 / token 成本线性膨胀。需要细节时模型应使用
          // filters.query 搜索相关实体，或用具体 scope + detailLevel=detailed。
          const chars = await window.api.getCharacters(context.bookId);
          const factions = await window.api.getFactions(context.bookId);
          const settings = await window.api.getSettings(context.bookId);
          const items = await window.api.getItems(context.bookId);
          const volumes = await window.api.getVolumes(context.bookId);

          const query = typeof filters.query === 'string' && filters.query.trim()
            ? filters.query.trim().toLowerCase()
            : '';
          const match = (s: string | null | undefined): boolean => !query || (s ?? '').toLowerCase().includes(query);
          const isDetailed = detailLevel === 'detailed';
          const isBrief = detailLevel === 'brief';

          const charData = chars
            .filter((c) => match(c.name) || match(c.description) || match(c.strength))
            .slice(0, 30)
            .map((c) => isDetailed
              ? { id: c.id, name: c.name, age: c.age, strength: c.strength, description: (c.description ?? '').slice(0, 500) || undefined }
              : { id: c.id, name: c.name, ...(isBrief ? {} : { strength: c.strength ?? undefined }) });
          const factionData = factions
            .filter((f) => match(f.name) || match(f.description))
            .slice(0, 30)
            .map((f) => isDetailed
              ? { id: f.id, name: f.name, parent_faction_id: f.parent_faction_id, description: (f.description ?? '').slice(0, 500) || undefined }
              : { id: f.id, name: f.name });
          const settingData = settings
            .filter((s) => match(s.title) || match(s.content))
            .slice(0, 30)
            .map((s) => isDetailed
              ? { id: s.id, title: s.title, content: (s.content ?? '').slice(0, 800) }
              : { id: s.id, title: s.title });
          const itemData = items
            .filter((it) => match(it.name) || match(it.description) || match(it.type))
            .slice(0, 30)
            .map((it) => isDetailed
              ? { id: it.id, name: it.name, type: it.type, description: (it.description ?? '').slice(0, 500) || undefined }
              : { id: it.id, name: it.name, type: it.type });

          // 章节时间线（标题 + 摘要，理解剧情走向）
          const timeline: Array<{ id: string; chapter_number: number; title: string; summary: string }> = [];
          for (const vol of volumes) {
            const chaps = await window.api.getChapters(vol.id);
            for (const c of chaps) {
              timeline.push({ id: c.id, chapter_number: c.chapter_number ?? 0, title: c.title, summary: c.summary || '' });
            }
          }
          timeline.sort((a, b) => a.chapter_number - b.chapter_number);

          // 当前章节全文（优先取编辑器实时内容）
          let currentChapter: { id: string; title: string; chapter_number: number; content: string; source: 'editor' | 'database' } | null = null;
          if (context.chapterId) {
            const meta = await window.api.getChapterMetadata(context.chapterId);
            if (meta && meta.book_id === context.bookId) {
              const content = context.editor
                ? context.editor.getText()
                : await window.api.getChapterContent(context.chapterId);
              currentChapter = {
                id: meta.id,
                title: meta.title,
                chapter_number: meta.chapter_number ?? 0,
                content,
                source: context.editor ? 'editor' : 'database',
              };
            }
          }

          const outline = (await window.api.getOutline(context.bookId)) || '';
          const summaryParts = [
            `人物 ${charData.length}/${chars.length}`,
            `势力 ${factionData.length}/${factions.length}`,
            `设定 ${settingData.length}/${settings.length}`,
            `物品 ${itemData.length}/${items.length}`,
            `章节 ${timeline.length}`,
          ];
          return {
            scope: 'context',
            currentChapter,
            timeline: timeline.slice(0, 100),
            characters: charData,
            factions: factionData,
            settings: settingData,
            items: itemData,
            volumes: volumes.map((v) => ({ id: v.id, title: v.title })),
            outline: outline.slice(0, 3000),
            query,
            truncated: {
              characters: chars.length > 30,
              factions: factions.length > 30,
              settings: settings.length > 30,
              items: items.length > 30,
              timeline: timeline.length > 100,
              outline: outline.length > 3000,
            },
            summary: `上下文注入完成：${summaryParts.join('、')}${query ? `；关键词搜索：${query}` : ''}`,
            usageTip: '当需要某个人物/设定的完整描述时，用 scope=character|setting 等 + filters.id 或 detailLevel=detailed 获取细节，不要重复拉取整个 context。',
          };
        }

        case 'character': {
          let chars = await window.api.getCharacters(context.bookId);
          if (filters.id) {
            const charId = filters.id as string;
            const found = chars.find((c) => c.id === charId);
            if (!found) return { error: `人物 ID ${charId} 不存在` };
            const isDetailed = detailLevel === 'detailed';
            const isBrief = detailLevel === 'brief';
            const data = isDetailed
              ? [{ id: found.id, name: found.name, age: found.age, strength: found.strength, description: found.description, faction_id: found.faction_id }]
              : isBrief ? [{ id: found.id, name: found.name }] : [{ id: found.id, name: found.name, strength: found.strength ?? undefined }];
            return { scope: 'character', data, count: 1, total: chars.length };
          }
          if (filters.name) chars = chars.filter((c) => c.name.includes(filters.name as string));
          const limit = (filters.limit as number | undefined) ?? 10;
          chars = chars.slice(0, limit);
          const isDetailed = detailLevel === 'detailed';
          const isBrief = detailLevel === 'brief';
          const data = isDetailed
            ? chars.map((c) => ({ id: c.id, name: c.name, age: c.age, strength: c.strength, description: c.description, faction_id: c.faction_id }))
            : isBrief ? chars.map((c) => ({ id: c.id, name: c.name })) : chars.map((c) => ({ id: c.id, name: c.name, strength: c.strength ?? undefined }));
          return { scope: 'character', data, count: data.length, total: chars.length };
        }

        case 'faction': {
          let forces = await window.api.getFactions(context.bookId);
          if (filters.id) {
            const facId = filters.id as string;
            const found = forces.find((f) => f.id === facId);
            if (!found) return { error: `势力 ID ${facId} 不存在` };
            const data = detailLevel === 'detailed'
              ? [{ id: found.id, name: found.name, description: found.description, parent_faction_id: found.parent_faction_id }]
              : [{ id: found.id, name: found.name }];
            return { scope: 'faction', data, count: 1, total: forces.length };
          }
          if (filters.name) forces = forces.filter((f) => f.name.includes(filters.name as string));
          const limit = (filters.limit as number | undefined) ?? 10;
          forces = forces.slice(0, limit);
          const data = detailLevel === 'detailed'
            ? forces.map((f) => ({ id: f.id, name: f.name, description: f.description, parent_faction_id: f.parent_faction_id }))
            : forces.map((f) => ({ id: f.id, name: f.name }));
          return { scope: 'faction', data, count: data.length, total: forces.length };
        }

        case 'setting': {
          let settings = await window.api.getSettings(context.bookId);
          if (filters.id) {
            const setId = filters.id as string;
            const found = settings.find((s) => s.id === setId);
            if (!found) return { error: `设定 ID ${setId} 不存在` };
            const data = detailLevel === 'detailed'
              ? [{ id: found.id, title: found.title, content: found.content }]
              : [{ id: found.id, title: found.title }];
            return { scope: 'setting', data, count: 1, total: settings.length };
          }
          if (filters.title) settings = settings.filter((s) => s.title.includes(filters.title as string));
          if (filters.category) settings = settings.filter((s) => s.title.includes(filters.category as string) || (s.content && s.content.includes(filters.category as string)));
          const limit = (filters.limit as number | undefined) ?? 10;
          settings = settings.slice(0, limit);
          const data = detailLevel === 'detailed'
            ? settings.map((s) => ({ id: s.id, title: s.title, content: s.content }))
            : settings.map((s) => ({ id: s.id, title: s.title }));
          return { scope: 'setting', data, count: data.length, total: settings.length };
        }

        case 'chapter': {
          const chapterFilterId = filters.id as string | undefined;
          if (chapterFilterId) {
            const ch = await window.api.getChapterMetadata(chapterFilterId);
            if (!ch || ch.book_id !== context.bookId) return { error: `章节 ID ${chapterFilterId} 不存在` };
            return {
              scope: 'chapter',
              data: [{ id: ch.id, chapter_number: ch.chapter_number, title: ch.title, summary: ch.summary, word_count: ch.word_count }],
              count: 1, total: 1,
            };
          }
          const volumes = await window.api.getVolumes(context.bookId);
          const allChapters: Array<Record<string, unknown>> = [];
          for (const vol of volumes) {
            const volumeId = filters.volumeId as string | undefined;
          if (volumeId && vol.id !== volumeId) continue;
          const chaps = await window.api.getChapters(vol.id);
          for (const c of chaps) {
            if (filters.title && !c.title.includes(filters.title as string)) continue;
            allChapters.push(detailLevel === 'detailed'
                ? { id: c.id, chapter_number: c.chapter_number, title: c.title, summary: c.summary, word_count: c.word_count, volume_id: vol.id, volume_title: vol.title }
                : { id: c.id, chapter_number: c.chapter_number, title: c.title, word_count: c.word_count, volume_id: vol.id });
            }
          }
          const limit = (filters.limit as number | undefined) ?? 10;
          const data = allChapters.slice(0, limit);
          return { scope: 'chapter', data, count: data.length, total: allChapters.length };
        }

        case 'volume': {
          let volumes = await window.api.getVolumes(context.bookId);
          if (filters.id) {
            const volId = filters.id as string;
            const found = volumes.find((v) => v.id === volId);
            if (!found) return { error: `分卷 ID ${volId} 不存在` };
            const data = detailLevel === 'detailed'
              ? [{ id: found.id, title: found.title, description: found.description, order_index: found.order_index }]
              : [{ id: found.id, title: found.title }];
            return { scope: 'volume', data, count: 1, total: volumes.length };
          }
          if (filters.title) volumes = volumes.filter((v) => v.title.includes(filters.title as string));
          const limit = (filters.limit as number | undefined) ?? 10;
          volumes = volumes.slice(0, limit);
          const data = detailLevel === 'detailed'
            ? volumes.map((v) => ({ id: v.id, title: v.title, description: v.description, order_index: v.order_index }))
            : volumes.map((v) => ({ id: v.id, title: v.title }));
          return { scope: 'volume', data, count: data.length, total: volumes.length };
        }

        case 'item': {
          let itemList = await window.api.getItems(context.bookId);
          if (filters.id) {
            const itemId = filters.id as string;
            const found = itemList.find((it) => it.id === itemId);
            if (!found) return { error: `物品 ID ${itemId} 不存在` };
            const isDetailed = detailLevel === 'detailed';
            const isBrief = detailLevel === 'brief';
            const data = isDetailed
              ? [{ id: found.id, name: found.name, type: found.type, description: found.description }]
              : isBrief ? [{ id: found.id, name: found.name }] : [{ id: found.id, name: found.name, type: found.type }];
            return { scope: 'item', data, count: 1, total: itemList.length };
          }
          if (filters.name) itemList = itemList.filter((it) => it.name.includes(filters.name as string));
          if (filters.category) itemList = itemList.filter((it) => it.type.includes(filters.category as string));
          const limit = (filters.limit as number | undefined) ?? 10;
          itemList = itemList.slice(0, limit);
          const isDetailed = detailLevel === 'detailed';
          const isBrief = detailLevel === 'brief';
          const data = isDetailed
            ? itemList.map((it) => ({ id: it.id, name: it.name, type: it.type, description: it.description }))
            : isBrief ? itemList.map((it) => ({ id: it.id, name: it.name })) : itemList.map((it) => ({ id: it.id, name: it.name, type: it.type }));
          return { scope: 'item', data, count: data.length, total: itemList.length };
        }

        case 'outline': {
          const outline = await window.api.getOutline(context.bookId);
          return { scope: 'outline', outline: outline || '（暂无大纲）', metadata: { timestamp: Date.now() } };
        }

        case 'book': {
          const books = await window.api.getBooks();
          const book = books.find((b) => b.id === context.bookId);
          if (!book) return { error: `作品 ID ${context.bookId} 不存在` };
          const volumes = await window.api.getVolumes(context.bookId);
          let chapterCount = 0;
          for (const vol of volumes) {
            const chaps = await window.api.getChapters(vol.id);
            chapterCount += chaps.length;
          }
          return {
            scope: 'book',
            bookInfo: { title: book.title, totalWordCount: book.word_count, volumeCount: volumes.length, chapterCount },
            metadata: { timestamp: Date.now() },
          };
        }

        case 'timeline': {
          const volumes = await window.api.getVolumes(context.bookId);
          const volumeId = filters.volumeId as string | undefined;
          if (volumeId) {
            const vol = volumes.find((v) => v.id === volumeId);
            if (!vol) return { error: `分卷 ID ${volumeId} 不存在` };
          }
          const allChapters: Array<{ id: string; chapter_number: number; title: string; summary: string; word_count: number; volume_title: string }> = [];
          for (const vol of volumes) {
            if (volumeId && vol.id !== volumeId) continue;
            const chaps = await window.api.getChapters(vol.id);
            for (const c of chaps) {
              allChapters.push({
                id: c.id, chapter_number: c.chapter_number ?? 0, title: c.title,
                summary: c.summary || '', word_count: c.word_count, volume_title: vol.title,
              });
            }
          }
          allChapters.sort((a, b) => a.chapter_number - b.chapter_number);
          return { scope: 'timeline', chapters: allChapters, total: allChapters.length, metadata: { timestamp: Date.now() } };
        }

        case 'chapter_content': {
          const chapterId = filters.id as string | undefined;
          if (!chapterId) return { error: '必须指定 filters.id（章节ID）' };
          const chapter = await window.api.getChapterMetadata(chapterId);
          if (!chapter || chapter.book_id !== context.bookId) return { error: `章节 ID ${chapterId} 不存在` };
          // 若正是当前打开的章节，优先取编辑器实时正文（可能有未保存改动）
          let chapterContent: string;
          let source: 'editor' | 'database';
          if (chapterId === context.chapterId && context.editor) {
            chapterContent = context.editor.getText();
            source = 'editor';
          } else {
            chapterContent = await window.api.getChapterContent(chapterId);
            source = 'database';
          }
          return {
            scope: 'chapter_content', id: chapterId, chapter_number: chapter.chapter_number,
            title: chapter.title, summary: chapter.summary || '', content: chapterContent,
            wordCount: chapterContent.replace(/\s/g, '').length, source,
            metadata: { timestamp: Date.now() },
          };
        }

        default:
          return { error: `不支持的查询范围：${scope}，可选值：context | character | faction | setting | chapter | volume | item | outline | book | timeline | chapter_content` };
      }
    }

    case 'execute': {
      const atomic = toolArgs.atomic as boolean | undefined;
      const description = toolArgs.description as string | undefined;
      const rawActions = toolArgs.actions;

      const schedulerResult = validateExecuteActions(rawActions);
      if (!schedulerResult.valid) {
        const errorMessage = formatSchedulerErrors(schedulerResult);
        return {
          success: false, error: errorMessage,
          validationErrors: schedulerResult.errors.map((e) => ({ code: e.code, message: e.message, dataPath: e.dataPath, expectedFormat: e.expectedFormat })),
          hint: '请使用 help 工具查看正确的数据格式规范。调用 help 无需参数。',
        };
      }

      const actions = schedulerResult.normalizedActions ?? [];

      const chapterOps = actions.filter((op) => (op.target as string) === 'chapter' && op.action !== 'create');
      if (chapterOps.length > 0) {
        return { success: false, blocked: true, error: `章节的修改和删除操作暂不支持通过 AI 助手进行。请使用页面上的章节管理功能。章节仅支持 create 操作，请移除 target="chapter" 的非创建操作后重试。` };
      }

      if (context.onRequestApproval) {
        const batchItems = await Promise.all(actions.map(async (a) => {
          let details: Record<string, unknown> = a.data && Object.keys(a.data).length > 0 ? { ...a.data } : {};
          if (a.action === 'delete' && a.targetId) {
            try {
              switch (a.target) {
                case 'character': {
                  const all = await window.api.getCharacters(context.bookId);
                  const e = all.find((c) => c.id === a.targetId);
                  if (e) details = { name: e.name, age: e.age, strength: e.strength, description: e.description };
                  break;
                }
                case 'faction': {
                  const all = await window.api.getFactions(context.bookId);
                  const e = all.find((f) => f.id === a.targetId);
                  if (e) details = { name: e.name, description: e.description };
                  break;
                }
                case 'setting': {
                  const all = await window.api.getSettings(context.bookId);
                  const e = all.find((s) => s.id === a.targetId);
                  if (e) details = { title: e.title, content: e.content };
                  break;
                }
                case 'item': {
                  const all = await window.api.getItems(context.bookId);
                  const e = all.find((it) => it.id === a.targetId);
                  if (e) details = { name: e.name, type: e.type, description: e.description };
                  break;
                }
                case 'volume': {
                  const all = await window.api.getVolumes(context.bookId);
                  const e = all.find((v) => v.id === a.targetId);
                  if (e) details = { title: e.title, description: e.description };
                  break;
                }
              }
            } catch { /* ignore */ }
          }
          if (a.target === 'chapter_content' && a.targetId) {
            try {
              const chapter = await window.api.getChapterMetadata(a.targetId);
              if (chapter) details = { ...details, chapterTitle: chapter.title };
            } catch { /* ignore */ }
          }
          return { type: a.action as 'create' | 'update' | 'delete', target: a.target as ApprovalRequest['target'], targetId: a.targetId, details };
        }));

        const answer = await context.onRequestApproval({
          type: batchItems[0]?.type ?? 'update',
          target: batchItems[0]?.target ?? 'character',
          description: description ?? `批量执行 ${actions.length} 个操作`,
          details: { total: actions.length, atomic: atomic ?? false },
          batchItems,
        });
        // ⚠️ 必须解构 `approved`：回调现在返回的是 `ApprovalAnswer` 对象（带用户意见）。
        // 直接 `if (!answer)` 判断是**永真**的（对象总是 truthy）—— 用户点"拒绝"会被当成
        // 同意执行写入。这条旧循环仍在 AI 对话页使用，属于真正的写入门禁。
        if (!answer.approved) return { error: '用户取消了操作' };
        // 用户的意见一并交给上层：Harness 侧会把它注入会话让 AI 读到；
        // 旧循环没有这条通道，至少记进调试日志，便于排查"AI 为什么不按我说的改"。
        if (answer.comment || answer.itemComments?.length) {
          debugLogger.user('APPROVAL_COMMENT', { comment: answer.comment, itemComments: answer.itemComments });
        }
      }

      const results: Array<{ index: number; action: string; target: string; targetId?: string; success: boolean; message: string }> = [];
      let succeeded = 0;
      let failed = 0;
      let touchedStructure = false;
      let touchedWorld = false;

      for (const [i, op] of actions.entries()) {
        try {
          const data = op.data ?? {};
          let result: Record<string, unknown>;

          if (op.target === 'chapter_content') {
            const chapterId = op.targetId;
            if (!chapterId) { result = { success: false, error: '更新章节内容需要提供 targetId' }; }
            else {
            const content = data.content as string | undefined;
            const title = data.title as string | undefined;
            const summary = data.summary as string | undefined;

            const chapter = await window.api.getChapterMetadata(chapterId);
            if (!chapter || chapter.book_id !== context.bookId) {
              result = { success: false, error: `章节 ID ${chapterId} 不存在` };
            } else if (!content && !title && !summary) {
              result = { success: false, error: '章节操作至少需要提供 data.content、data.title 或 data.summary 之一' };
            } else if (!content && (title || summary)) {
              const newTitle = title ?? chapter.title;
              const newSummary = summary ?? (chapter.summary ?? '');
              await window.api.updateChapter({ id: chapterId, title: newTitle, summary: newSummary, content: chapter.content || '' });
              touchedStructure = true;
              if (context.chapterId === chapterId) context.onAutoSave?.();
              result = { success: true, action: op.action, target: op.target, targetId: chapterId, message: `章节《${chapter.title}》元数据已更新` };
            } else if (content) {
              const newTitle = title ?? chapter.title;
              const newSummary = summary ?? (chapter.summary ?? '');

              // 修改前快照：留档 AI 覆盖前的状态（回滚点）
              try {
                await ensureVersionQuota(chapterId, 'ai_modify');
                await window.api.createChapterVersion({
                  chapterId, bookId: chapter.book_id,
                  title: chapter.title, summary: chapter.summary ?? '',
                  content: chapter.content || '', wordCount: chapter.word_count, source: 'ai_modify',
                });
              } catch { /* 版本保存失败不阻塞主流程 */ }

              await window.api.updateChapter({ id: chapterId, title: newTitle, summary: newSummary, content });
              touchedStructure = true;

              // 修改后快照：把审批通过的“新内容”也留档，版本历史可立即看到最新状态
              try {
                await ensureVersionQuota(chapterId, 'ai_modify');
                await window.api.createChapterVersion({
                  chapterId, bookId: chapter.book_id,
                  title: newTitle, summary: newSummary,
                  content, wordCount: content.replace(/\s/g, '').length, source: 'ai_modify',
                });
              } catch { /* 版本保存失败不阻塞主流程 */ }

              // 若正是当前打开章节，回写编辑器并持久化格式。
              // 内容→HTML 的转换走 utils/editorContent（**幂等**：已是 `<p>…</p>` 的正文
              // 不会再被包一层，避免正文开头/末尾多出空段落——见该文件的事故说明）。
              // emitUpdate:false —— 内容已落库，程序化回写不应触发 onUpdate（防误判“未保存”）。
              if (context.chapterId === chapterId && context.editor) {
                writeChapterContentToEditor(context.editor, content);
                context.onAutoSave?.();
              }

              result = { success: true, action: op.action, target: op.target, targetId: chapterId, message: `章节《${chapter.title}》已更新` };
            } else {
              result = { success: false, error: '章节操作失败：未知状态' };
            }
            }
          } else {
            switch (`${op.action}:${op.target}`) {
              case 'create:volume': {
                const title = data.title as string | undefined;
                if (!title) { result = { success: false, error: '创建卷需要提供 data.title' }; break; }
                const { id: newId } = await window.api.createVolume({ bookId: context.bookId, title, description: (data.description as string | undefined) ?? '' });
                touchedStructure = true;
                result = { success: true, action: op.action, target: op.target, targetId: newId, message: `分卷《${title}》已创建 (ID: ${newId})` };
                break;
              }
              case 'create:chapter': {
                const title = data.title as string | undefined;
                const volumeId = data.volume_id as string | undefined;
                if (!title) { result = { success: false, error: '创建章节需要提供 data.title' }; break; }
                if (!volumeId) { result = { success: false, error: '创建章节需要提供 data.volume_id（所属卷的数据库 ID），请先通过 query scope=volume 查询获取卷 ID' }; break; }
                const volumes = await window.api.getVolumes(context.bookId);
                const targetVolume = volumes.find((v) => v.id === volumeId);
                if (!targetVolume) { result = { success: false, error: `卷 ID ${volumeId} 不存在或不属于当前作品` }; break; }
                const { lastInsertRowid: newId } = await window.api.createChapter({
                  volumeId,
                  title,
                  summary: (data.summary as string | undefined) ?? '',
                  content: (data.content as string | undefined) ?? '',
                  afterChapterNumber: data.after_chapter_number as number | undefined,
                });
                touchedStructure = true;
                result = { success: true, action: op.action, target: op.target, targetId: newId, message: `章节《${title}》已在《${targetVolume.title}》中创建 (ID: ${newId})` };
                break;
              }
              case 'delete:volume': {
                if (!op.targetId) { result = { success: false, error: '删除卷需要提供 targetId' }; break; }
                const volumes = await window.api.getVolumes(context.bookId);
                const volume = volumes.find((v) => v.id === op.targetId);
                if (!volume) { result = { success: false, error: `分卷 ID ${op.targetId} 不存在` }; break; }
                const chaps = await window.api.getChapters(op.targetId);
                await window.api.deleteVolume(op.targetId);
                touchedStructure = true;
                result = { success: true, action: op.action, target: op.target, targetId: op.targetId, message: `分卷《${volume.title}》已删除（含 ${chaps.length} 章）` };
                break;
              }
              case 'create:character': {
                const name = data.name as string | undefined;
                if (!name) { result = { success: false, error: '创建人物需要提供 data.name' }; break; }
                const { id: newId } = await window.api.createCharacter({
                  bookId: context.bookId, name,
                  age: (data.age as string | null | undefined) ?? null,
                  factionId: (data.faction_id as string | null | undefined) ?? null,
                  strength: (data.strength as string | null | undefined) ?? null,
                  description: (data.description as string | undefined) ?? '',
                });
                touchedWorld = true;
                result = { success: true, action: op.action, target: op.target, targetId: newId, message: `人物《${name}》已创建 (ID: ${newId})` };
                break;
              }
              case 'update:character': {
                if (!op.targetId) { result = { success: false, error: '更新人物需要提供 targetId' }; break; }
                const all = await window.api.getCharacters(context.bookId);
                const orig = all.find((c) => c.id === op.targetId);
                if (!orig) { result = { success: false, error: `人物 ID ${op.targetId} 不存在` }; break; }
                await window.api.updateCharacter({
                  id: op.targetId,
                  name: (data.name as string | undefined) ?? orig.name,
                  age: (data.age as string | null | undefined) ?? orig.age,
                  factionId: (data.faction_id as string | null | undefined) ?? orig.faction_id,
                  strength: (data.strength as string | null | undefined) ?? orig.strength,
                  description: (data.description as string | undefined) ?? orig.description ?? '',
                });
                touchedWorld = true;
                result = { success: true, action: op.action, target: op.target, targetId: op.targetId, message: `人物《${orig.name}》已更新` };
                break;
              }
              case 'delete:character': {
                if (!op.targetId) { result = { success: false, error: '删除人物需要提供 targetId' }; break; }
                const all = await window.api.getCharacters(context.bookId);
                const orig = all.find((c) => c.id === op.targetId);
                if (!orig) { result = { success: false, error: `人物 ID ${op.targetId} 不存在` }; break; }
                await window.api.deleteCharacter(op.targetId);
                touchedWorld = true;
                result = { success: true, action: op.action, target: op.target, targetId: op.targetId, message: `人物《${orig.name}》已删除` };
                break;
              }
              case 'create:faction': {
                const name = data.name as string | undefined;
                if (!name) { result = { success: false, error: '创建势力需要提供 data.name' }; break; }
                const { id: newId } = await window.api.createFaction({
                  bookId: context.bookId, name,
                  parentFactionId: (data.parent_faction_id as string | null | undefined) ?? null,
                  description: (data.description as string | undefined) ?? '',
                });
                touchedWorld = true;
                result = { success: true, action: op.action, target: op.target, targetId: newId, message: `势力《${name}》已创建 (ID: ${newId})` };
                break;
              }
              case 'update:faction': {
                if (!op.targetId) { result = { success: false, error: '更新势力需要提供 targetId' }; break; }
                const all = await window.api.getFactions(context.bookId);
                const orig = all.find((f) => f.id === op.targetId);
                if (!orig) { result = { success: false, error: `势力 ID ${op.targetId} 不存在` }; break; }
                await window.api.updateFaction({
                  id: op.targetId,
                  name: (data.name as string | undefined) ?? orig.name,
                  parentFactionId: (data.parent_faction_id as string | null | undefined) ?? orig.parent_faction_id,
                  description: (data.description as string | undefined) ?? orig.description ?? '',
                });
                touchedWorld = true;
                result = { success: true, action: op.action, target: op.target, targetId: op.targetId, message: `势力《${orig.name}》已更新` };
                break;
              }
              case 'delete:faction': {
                if (!op.targetId) { result = { success: false, error: '删除势力需要提供 targetId' }; break; }
                const all = await window.api.getFactions(context.bookId);
                const orig = all.find((f) => f.id === op.targetId);
                if (!orig) { result = { success: false, error: `势力 ID ${op.targetId} 不存在` }; break; }
                await window.api.deleteFaction(op.targetId);
                touchedWorld = true;
                result = { success: true, action: op.action, target: op.target, targetId: op.targetId, message: `势力《${orig.name}》已删除` };
                break;
              }
              case 'create:setting': {
                const title = data.title as string | undefined;
                if (!title) { result = { success: false, error: '创建世界设定需要提供 data.title' }; break; }
                const { id: newId } = await window.api.createSetting({ bookId: context.bookId, title, content: (data.content as string | undefined) ?? '' });
                touchedWorld = true;
                result = { success: true, action: op.action, target: op.target, targetId: newId, message: `世界设定《${title}》已创建 (ID: ${newId})` };
                break;
              }
              case 'update:setting': {
                if (!op.targetId) { result = { success: false, error: '更新世界设定需要提供 targetId' }; break; }
                const all = await window.api.getSettings(context.bookId);
                const orig = all.find((s) => s.id === op.targetId);
                if (!orig) { result = { success: false, error: `设定 ID ${op.targetId} 不存在` }; break; }
                await window.api.updateSetting({
                  id: op.targetId,
                  title: (data.title as string | undefined) ?? orig.title,
                  content: (data.content as string | undefined) ?? orig.content ?? '',
                });
                touchedWorld = true;
                result = { success: true, action: op.action, target: op.target, targetId: op.targetId, message: `世界设定《${orig.title}》已更新` };
                break;
              }
              case 'delete:setting': {
                if (!op.targetId) { result = { success: false, error: '删除世界设定需要提供 targetId' }; break; }
                const all = await window.api.getSettings(context.bookId);
                const orig = all.find((s) => s.id === op.targetId);
                if (!orig) { result = { success: false, error: `设定 ID ${op.targetId} 不存在` }; break; }
                await window.api.deleteSetting(op.targetId);
                touchedWorld = true;
                result = { success: true, action: op.action, target: op.target, targetId: op.targetId, message: `世界设定《${orig.title}》已删除` };
                break;
              }
              case 'create:item': {
                const name = data.name as string | undefined;
                if (!name) { result = { success: false, error: '创建物品需要提供 data.name' }; break; }
                const { id: newId } = await window.api.createItem({
                  bookId: context.bookId, name,
                  type: (data.type as string | undefined) ?? '道具',
                  description: (data.description as string | undefined) ?? '',
                });
                touchedWorld = true;
                result = { success: true, action: op.action, target: op.target, targetId: newId, message: `物品《${name}》已创建 (ID: ${newId})` };
                break;
              }
              case 'update:item': {
                if (!op.targetId) { result = { success: false, error: '更新物品需要提供 targetId' }; break; }
                const all = await window.api.getItems(context.bookId);
                const orig = all.find((it) => it.id === op.targetId);
                if (!orig) { result = { success: false, error: `物品 ID ${op.targetId} 不存在` }; break; }
                await window.api.updateItem({
                  id: op.targetId,
                  name: (data.name as string | undefined) ?? orig.name,
                  type: (data.type as string | undefined) ?? orig.type,
                  description: (data.description as string | undefined) ?? orig.description ?? '',
                });
                touchedWorld = true;
                result = { success: true, action: op.action, target: op.target, targetId: op.targetId, message: `物品《${orig.name}》已更新` };
                break;
              }
              case 'delete:item': {
                if (!op.targetId) { result = { success: false, error: '删除物品需要提供 targetId' }; break; }
                const all = await window.api.getItems(context.bookId);
                const orig = all.find((it) => it.id === op.targetId);
                if (!orig) { result = { success: false, error: `物品 ID ${op.targetId} 不存在` }; break; }
                await window.api.deleteItem(op.targetId);
                touchedWorld = true;
                result = { success: true, action: op.action, target: op.target, targetId: op.targetId, message: `物品《${orig.name}》已删除` };
                break;
              }
              case 'update:outline': {
                const outlineContent = data.content as string | undefined;
                if (!outlineContent) { result = { success: false, error: '更新大纲需要提供 data.content' }; break; }
                await window.api.updateOutline({ bookId: context.bookId, content: outlineContent });
                touchedWorld = true;
                result = { success: true, action: op.action, target: op.target, message: `故事大纲已更新（${outlineContent.length} 字符）` };
                break;
              }
              default:
                result = { success: false, error: `不支持的操作：${op.action}:${op.target}` };
                break;
            }
          }

          if (result.success) {
            succeeded++;
            results.push({ index: i + 1, action: op.action, target: op.target, targetId: op.targetId, success: true, message: String(result.message ?? '操作成功') });
          } else {
            failed++;
            const errMsg = String(result.error ?? '操作失败');
            results.push({ index: i + 1, action: op.action, target: op.target, targetId: op.targetId, success: false, message: errMsg });
            if (atomic) break;
          }
        } catch (e) {
          failed++;
          const errMsg = e instanceof Error ? e.message : '操作执行异常';
          results.push({ index: i + 1, action: op.action, target: op.target, targetId: op.targetId, success: false, message: errMsg });
          if (atomic) break;
        }
      }

      // 数据已写库，通知界面刷新（资料库面板 / 章节结构侧边栏）
      if (touchedWorld) context.onWorldRefresh?.();
      if (touchedStructure) context.onChapterListRefresh?.();

      const overallSuccess = atomic ? failed === 0 : succeeded > 0;
      return {
        success: overallSuccess, results,
        summary: { total: actions.length, succeeded, failed },
        ...(failed > 0 ? { failedOperations: results.filter((r) => !r.success) } : {}),
      };
    }

    case 'help': {
      const helpContent = getHelpContent();
      return { tool: 'help', content: helpContent, summary: '已返回完整的工具使用说明和数据规范文档' };
    }

    default:
      debugLogger.toolError('TOOL_UNKNOWN', { toolName });
      throw new Error(`未知工具：${toolName}`);
  }
}

export { XIAOTIANSHU_TOOLS };
